/**
 * Authentication Redux Slice for Rider Gateway (Mobile)
 * Mobile-optimized state management with offline support
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/auth/cognitoAuth';
import { AuthError } from '../../services/auth/authConfig';
import type { JWTUser, AuthErrorInfo } from '../../services/auth/authConfig';

// Mobile-specific auth state
export interface AuthState {
  user: JWTUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isOffline: boolean;
  error: AuthErrorInfo | null;
  lastActivity: number;
  biometricEnabled: boolean;
  rememberDevice: boolean;
  offlineUser: JWTUser | null; // Cached user for offline mode
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  isOffline: false,
  error: null,
  lastActivity: Date.now(),
  biometricEnabled: false,
  rememberDevice: false,
  offlineUser: null,
};

// Async thunks for mobile authentication

/**
 * Initialize authentication state (mobile-optimized)
 */
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const isOffline = authService.isOffline();
      const hasValidSession = await authService.ensureValidTokens();
      const user = hasValidSession ? await authService.getCurrentUser() : null;
      const isAuthenticated = hasValidSession && user !== null;
      const biometricEnabled = await authService.isBiometricAvailable();
      const rememberDevice = authService.shouldRememberDevice();
      
      return {
        user,
        isAuthenticated,
        isOffline,
        biometricEnabled,
        rememberDevice,
      };
    } catch (error: any) {
      return rejectWithValue({
        type: AuthError.NETWORK_ERROR,
        message: 'Failed to initialize mobile authentication',
        details: error,
      });
    }
  }
);

/**
 * Handle OAuth callback (mobile-optimized)
 */
export const handleAuthCallback = createAsyncThunk(
  'auth/handleCallback',
  async (urlParams: URLSearchParams, { rejectWithValue }) => {
    try {
      const user = await authService.handleCallback(urlParams);
      return user;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Initiate login flow (mobile-optimized)
 */
export const login = createAsyncThunk(
  'auth/login',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      
      // Check if offline
      if (state.auth.isOffline) {
        throw {
          type: AuthError.OFFLINE_MODE,
          message: 'Cannot login while offline',
        };
      }
      
      await authService.login();
      return null;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Logout user (mobile-optimized)
 */
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return null;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Refresh tokens (mobile-optimized)
 */
export const refreshTokens = createAsyncThunk(
  'auth/refreshTokens',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      
      // Don't refresh in offline mode
      if (state.auth.isOffline) {
        throw {
          type: AuthError.OFFLINE_MODE,
          message: 'Cannot refresh tokens while offline',
        };
      }
      
      await authService.refreshTokens();
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Check and refresh tokens (mobile-optimized)
 */
export const ensureValidTokens = createAsyncThunk(
  'auth/ensureValidTokens',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      
      // In offline mode, use cached user
      if (state.auth.isOffline) {
        return state.auth.offlineUser;
      }
      
      const isValid = await authService.ensureValidTokens();
      if (!isValid) {
        throw {
          type: AuthError.TOKEN_EXPIRED,
          message: 'Tokens are no longer valid',
        };
      }
      
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Enable biometric authentication
 */
export const enableBiometric = createAsyncThunk(
  'auth/enableBiometric',
  async (_, { rejectWithValue }) => {
    try {
      const success = await authService.enableBiometric();
      return success;
    } catch (error: any) {
      return rejectWithValue({
        type: AuthError.BIOMETRIC_FAILED,
        message: 'Failed to enable biometric authentication',
        details: error,
      });
    }
  }
);

/**
 * Handle network status change
 */
export const updateNetworkStatus = createAsyncThunk(
  'auth/updateNetworkStatus',
  async (isOnline: boolean, { getState }) => {
    const state = getState() as { auth: AuthState };
    
    if (isOnline && state.auth.isOffline) {
      // Coming back online - try to refresh tokens
      try {
        await authService.ensureValidTokens();
        const user = await authService.getCurrentUser();
        return { isOnline, user };
      } catch (error) {
        console.warn('Failed to refresh tokens when coming online:', error);
        return { isOnline, user: state.auth.user };
      }
    }
    
    return { isOnline, user: state.auth.user };
  }
);

// Mobile auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear authentication error
    clearAuthError: (state) => {
      state.error = null;
    },
    
    // Update last activity
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },
    
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // Clear auth state
    clearAuthState: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.offlineUser = null;
      state.lastActivity = Date.now();
    },
    
    // Set offline mode
    setOfflineMode: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
      
      // When going offline, cache current user
      if (action.payload && state.user) {
        state.offlineUser = state.user;
      }
    },
    
    // Set remember device preference
    setRememberDevice: (state, action: PayloadAction<boolean>) => {
      state.rememberDevice = action.payload;
      authService.setRememberDevice(action.payload);
    },
    
    // Update biometric status
    updateBiometricStatus: (state, action: PayloadAction<boolean>) => {
      state.biometricEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.user = action.payload.user;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.isOffline = action.payload.isOffline;
        state.biometricEnabled = action.payload.biometricEnabled;
        state.rememberDevice = action.payload.rememberDevice;
        state.lastActivity = Date.now();
        
        // Cache user for offline mode
        if (action.payload.user) {
          state.offlineUser = action.payload.user;
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload as AuthErrorInfo;
      });

    // Handle auth callback
    builder
      .addCase(handleAuthCallback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(handleAuthCallback.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.offlineUser = action.payload; // Cache for offline
        state.lastActivity = Date.now();
      })
      .addCase(handleAuthCallback.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload as AuthErrorInfo;
      });

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as AuthErrorInfo;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.offlineUser = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as AuthErrorInfo;
        // Clear state even if logout fails
        state.user = null;
        state.isAuthenticated = false;
        state.offlineUser = null;
      });

    // Refresh tokens
    builder
      .addCase(refreshTokens.pending, (state) => {
        state.error = null;
      })
      .addCase(refreshTokens.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.offlineUser = action.payload; // Update cache
        state.lastActivity = Date.now();
      })
      .addCase(refreshTokens.rejected, (state, action) => {
        state.error = action.payload as AuthErrorInfo;
        state.user = null;
        state.isAuthenticated = false;
      });

    // Ensure valid tokens
    builder
      .addCase(ensureValidTokens.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
          state.offlineUser = action.payload;
          state.lastActivity = Date.now();
        }
      })
      .addCase(ensureValidTokens.rejected, (state, action) => {
        state.error = action.payload as AuthErrorInfo;
        if (!state.isOffline) {
          state.user = null;
          state.isAuthenticated = false;
        }
      });

    // Enable biometric
    builder
      .addCase(enableBiometric.fulfilled, (state, action) => {
        state.biometricEnabled = action.payload;
      })
      .addCase(enableBiometric.rejected, (state, action) => {
        state.error = action.payload as AuthErrorInfo;
        state.biometricEnabled = false;
      });

    // Update network status
    builder
      .addCase(updateNetworkStatus.fulfilled, (state, action) => {
        state.isOffline = !action.payload.isOnline;
        if (action.payload.user) {
          state.user = action.payload.user;
          if (action.payload.isOnline) {
            state.offlineUser = action.payload.user;
          }
        }
      });
  },
});

// Export actions
export const {
  clearAuthError,
  updateLastActivity,
  setLoading,
  clearAuthState,
  setOfflineMode,
  setRememberDevice,
  updateBiometricStatus,
} = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => 
  state.auth.isOffline ? state.auth.offlineUser : state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectIsInitialized = (state: { auth: AuthState }) => state.auth.isInitialized;
export const selectIsOffline = (state: { auth: AuthState }) => state.auth.isOffline;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectLastActivity = (state: { auth: AuthState }) => state.auth.lastActivity;
export const selectBiometricEnabled = (state: { auth: AuthState }) => state.auth.biometricEnabled;
export const selectRememberDevice = (state: { auth: AuthState }) => state.auth.rememberDevice;

// Utility selectors
export const selectUserType = (state: { auth: AuthState }) => {
  const user = selectUser(state);
  return user?.['custom:user_type'];
};
export const selectUserEmail = (state: { auth: AuthState }) => {
  const user = selectUser(state);
  return user?.email;
};
export const selectUserGroups = (state: { auth: AuthState }) => {
  const user = selectUser(state);
  return user?.['cognito:groups'] || [];
};
export const selectIsRider = (state: { auth: AuthState }) => {
  const user = selectUser(state);
  const userType = user?.['custom:user_type'];
  return userType === 'individual' || userType === 'fleet';
};

// Mobile-specific selectors
export const selectCanUseOfflineFeatures = (state: { auth: AuthState }) => 
  state.auth.isOffline && state.auth.offlineUser !== null;

export const selectNetworkStatus = (state: { auth: AuthState }) => ({
  isOffline: state.auth.isOffline,
  hasOfflineData: state.auth.offlineUser !== null,
});

// Export reducer
export default authSlice.reducer;
